import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/data';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 91n com 官方入口指南',
  description: '了解 91n com 官方入口指南的品牌故事、信誉数据和联系方式。我们致力于为用户提供最新、最准确的平台访问信息。',
  url: 'https://91n-guide.com/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const breadcrumbItems = [{ label: '首页', path: '/' }, { label: '关于我们' }];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pm539n2trhb35gvhog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20team%20collaboration%20concept%20with%20warm%20amber%20lighting%2C%20modern%20office%20abstract%2C%20clean%20minimal%20aesthetic%2C%20trust%20and%20reliability%20theme%2C%20soft%20atmosphere&width=1600&height=400&seq=about-banner&orientation=landscape"
            alt="关于我们"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">关于我们</h1>
            <p className="text-background-50/80 text-sm md:text-base">了解 91n com 官方入口指南</p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">品牌介绍</h2>
              <div className="prose max-w-none">
                {aboutInfo.intro.split('\n').filter(Boolean).map((paragraph, index) => (
                  <p key={index} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">信誉数据</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {aboutInfo.reputation.map((item) => (
                  <div
                    key={item.label}
                    className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{item.value}</div>
                    <div className="text-foreground-500 text-xs">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-950 text-sm mb-1">邮箱</h4>
                      <p className="text-foreground-600 text-sm">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-950 text-sm mb-1">微信</h4>
                      <p className="text-foreground-600 text-sm">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                      <i className="ri-qq-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-950 text-sm mb-1">QQ</h4>
                      <p className="text-foreground-600 text-sm">{aboutInfo.contact.qq}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-950 text-sm mb-1">地址</h4>
                      <p className="text-foreground-600 text-sm">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="font-semibold text-foreground-950 mb-5 text-base">发送消息</h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-foreground-950 font-semibold text-sm mb-1">发送成功</p>
                      <p className="text-foreground-500 text-xs">感谢您的留言，我们会尽快回复</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            id="about-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            id="about-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                          <textarea
                            id="about-message"
                            name="message"
                            required
                            rows={4}
                            maxLength={500}
                            className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                            placeholder="请输入您的留言内容（最多500字）"
                          ></textarea>
                        </div>
                        <div className="honeypot-field">
                          <input
                            type="text"
                            name="website_alt"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                          />
                        </div>
                        {formStatus === 'error' && (
                          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{formError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                        >
                          {isSubmitting ? '发送中...' : '发送消息'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}